import * as React from "react";
import Select from "react-select";

import { ROLE_ADMIN, isInRole } from "@/lib/Auth";
import { getBackend } from "@/lib/backend/Backend2";
import { niceNumber } from "@/lib/Util";
import { translate } from "@/components/IntlProviderWrapper";
import { getPlantsForUser } from "@/lib/BusinessLogic";
import { PRODUCTION_STATUSES } from "@/lib/ManufacturingConsts.generated";
import OrderTableProductionModal from "@/components/manufacturing/OrderTableProductionModal";
import LargeDataTable, {
    ColumnId,
    FilterParser,
    getFieldNameMax,
    getFieldNameMin,
    formatBoolean,
    formatDateTime,
    formatMaterialTitle,
    DateRangeFilter,
    TextFilter,
    MultiSelectFilter,
    SelectFilter,
    BooleanCell,
    FloatCell,
    DateTimeCell,
    MaterialTitleCell,
    TextFilterWithReset,
    DefaultFiltersForm,
    DEFAULT_PAGE_SIZE,
} from "@/components/manufacturing/LargeDataTable";

import type { IFilterProducedReq, IProducedModel } from "@/lib/backend/manufacturing2.generated.types";
import type {
    Filters,
    TableColumn,
    TableCellProps,
    TableExportData,
    TableFooter,
    TableFooterProps,
} from "@/components/manufacturing/LargeDataTable";
import type { ILabelValuePair } from "@/lib/Util";

enum QUANTITY_SCOPE {
    page = "page",
    all = "all",
}

type TableRow = {
    ts: number;
    available_in_ts: number;
    quantity_produced: number;
    quantity_scrap: number;
    status: string;
    stock_accounted_for: boolean;
    uuid: string;
    order_external_id: string;
    external_operation_id: string;
    order_title: string;
    process_num: string;
    material_external_id: string;
    material_title: string;
    line_external_id: string;
    line_title: string;
    plant_external_id: string;
    plant_title: string;
    produced_end: number;
    produced_start: number;
};

type Props = {};

type State = {
    default_filters: Filters<TableRow> | null;
    error: string;
    is_loading: boolean;
    is_updating: boolean;
    total_count: number;
    total_quantity_produced: number;
    total_quantity_scrap: number;
    total_quantity_scope: QUANTITY_SCOPE;
    rows: TableRow[];
    show_details: boolean;
    selected_row: TableRow | null;
    show_order_modal: boolean;
    is_scrolled_x: boolean;
};

const DATA_TABLE_ID = "production-data-productions";

const getDefaultFilters = (start_date: Date): Filters<TableRow> => {
    return {
        status: [PRODUCTION_STATUSES.confirmed, PRODUCTION_STATUSES.unconfirmed].join(","),
        [getFieldNameMin("produced_start")]: start_date.toISOString(),
    } as Filters<TableRow>;
};

const getFiterRequestParams = (
    filters: Filters<TableRow>,
    page_size?: number,
    page_index?: number,
    order_by?: ColumnId<TableRow>,
    order_desc?: boolean,
    count_filtered?: boolean,
    aggregate_filtered?: boolean
): IFilterProducedReq => {
    const filter_parser = new FilterParser(filters);
    const filter_params: IFilterProducedReq = {
        plant_uuids: getPlantsForUser().map(plant => plant.uuid),
        order_external_id: filter_parser.get("order_external_id"),
        external_operation_id: filter_parser.get("external_operation_id"),
        order_title: filter_parser.get("order_title"),
        process_num: filter_parser.get("process_num"),
        material_external_id: filter_parser.get("material_external_id"),
        material_title: filter_parser.get("material_title"),
        line_external_id: filter_parser.get("line_external_id"),
        line_title: filter_parser.get("line_title"),
        plant_external_id: filter_parser.get("plant_external_id"),
        plant_title: filter_parser.get("plant_title"),
        produced_start_min: filter_parser.getTimestamp(getFieldNameMin("produced_start")),
        produced_start_max: filter_parser.getTimestamp(getFieldNameMax("produced_start")),
        produced_end_min: filter_parser.getTimestamp(getFieldNameMin("produced_end")),
        produced_end_max: filter_parser.getTimestamp(getFieldNameMax("produced_end")),
        stock_accounted_for: filter_parser.getBoolean("stock_accounted_for"),
        status: filter_parser.getOptions("status"),
        limit: page_size,
        offset: page_size !== undefined && page_index !== undefined ? page_index * page_size : undefined,
        order_by,
        order_desc,
        count_filtered,
        aggregate_filtered,
    };

    return filter_params;
};

const mapRow = (production: IProducedModel): TableRow => {
    return {
        available_in_ts: production.available_in_ts,
        external_operation_id: production.external_operation_id,
        line_external_id: production.line_external_id,
        line_title: production.line_title,
        material_external_id: production.material_external_id,
        material_title: production.material_title,
        order_external_id: production.order_external_id || "",
        order_title: production.order_title,
        plant_external_id: production.plant_external_id,
        plant_title: production.plant_title,
        process_num: production.process_num,
        produced_end: production.produced_end,
        produced_start: production.produced_start,
        quantity_produced: production.quantity_produced,
        quantity_scrap: production.quantity_scrap,
        status: production.status,
        stock_accounted_for: production.stock_accounted_for,
        ts: production.ts,
        uuid: production.uuid,
    };
};

const formatStatus = (props: TableCellProps<TableRow>): string => {
    const { row } = props;
    const value = row.status;
    return translate(`common.${value}`, `${value}`);
};

const StatusCell = (props: TableCellProps<TableRow>) => {
    return <span>{formatStatus(props)}</span>;
};

const ExternalIdCell = (props: TableCellProps<TableRow> & { onShowOrderModal: (row: TableRow) => void }) => {
    const { row, onShowOrderModal } = props;
    const value = row.order_external_id;
    return (
        <div className="large-data-table__external-id">
            <button
                className="btn btn-short btn-outline-secondary fas fa-eye mr-2"
                title={translate("common.tooltip_show_details", "Show details")}
                onClick={() => onShowOrderModal(row)}
            />
            <span>{value}</span> <b className="text-muted"> {row.process_num}</b>
        </div>
    );
};

const FloatFooter = (props: TableFooterProps<TableRow>) => {
    const { column, footer, table_props } = props;
    const value = Number(footer[column.id]);
    if (value < 0) {
        if (table_props.is_updating) {
            return <span className="fas fa-spinner fa-spin" />;
        }

        return null;
    }

    return <span>{niceNumber(value, 2)}</span>;
};

const ExternalIdFooter = (
    props: TableFooterProps<TableRow> & {
        total_quantity_produced: number;
        total_quantity_scope: QUANTITY_SCOPE;
        onTotalQuantityScopeChange: (selected: ILabelValuePair) => void;
        onDataLoad: (
            filters: Filters<TableRow>,
            page_index: number,
            page_size: number,
            order_by?: ColumnId<TableRow>,
            order_desc?: boolean,
            is_updating?: boolean,
            aggregate_filtered?: boolean
        ) => Promise<void>;
    }
) => {
    const scope_options: ILabelValuePair[] = [
        {
            value: QUANTITY_SCOPE.page,
            label: translate("common.current_page", "Current page"),
        },
        {
            value: QUANTITY_SCOPE.all,
            label: translate("common.all", "All"),
        },
    ];
    const { column, footer, table_props, total_quantity_produced, total_quantity_scope, onTotalQuantityScopeChange } =
        props;
    const selected_scope = scope_options.find(option => option.value === total_quantity_scope);

    const handleScopeChange = (selected: ILabelValuePair) => {
        onTotalQuantityScopeChange(selected);
        if (selected.value === QUANTITY_SCOPE.all && total_quantity_produced < 0) {
            props.onDataLoad(
                props.filters,
                props.page_index,
                props.page_size,
                props.order_by,
                props.order_desc,
                true,
                true
            );
        }
    };

    return (
        <label className="d-flex align-items-center mb-0">
            <span className="mr-3">{footer[column.id]}</span>
            <Select
                className="flex-grow-1"
                name={column.id}
                value={selected_scope}
                options={scope_options}
                menuPosition="fixed"
                theme={theme => ({
                    ...theme,
                    spacing: {
                        ...theme.spacing,
                        baseUnit: 2,
                        controlHeight: 30,
                    },
                })}
                isDisabled={table_props.is_updating}
                onChange={handleScopeChange}
            />
        </label>
    );
};

class Productions extends React.Component<Props, State> {
    state: State = {
        default_filters: null,
        error: "",
        is_loading: false,
        is_updating: false,
        total_count: -1,
        total_quantity_produced: -1,
        total_quantity_scrap: -1,
        total_quantity_scope: QUANTITY_SCOPE.page,
        rows: [],
        show_details: false,
        selected_row: null,
        show_order_modal: false,
        is_scrolled_x: false,
    };

    loadData = async (
        filters: Filters<TableRow>,
        page_index: number,
        page_size: number,
        order_by?: ColumnId<TableRow>,
        order_desc?: boolean,
        is_updating = false,
        aggregate_filtered = false
    ) => {
        this.setState({
            is_loading: !is_updating,
            is_updating,
        });
        try {
            const { productions, count, quantity_produced, quantity_scrap } =
                await getBackend().manufacturing.filterProduced(
                    getFiterRequestParams(
                        filters,
                        page_size,
                        page_index,
                        order_by,
                        order_desc,
                        false,
                        aggregate_filtered
                    )
                );

            const total_count = count !== undefined ? count : this.state.total_count;
            const page_productions =
                page_index < 0 && count !== undefined
                    ? productions.slice(Math.floor(count / page_size) * page_size - count)
                    : productions;
            const total_quantity_produced =
                quantity_produced !== undefined ? quantity_produced : this.state.total_quantity_produced;
            const total_quantity_scrap =
                quantity_scrap !== undefined ? quantity_scrap : this.state.total_quantity_scrap;
            const rows: TableRow[] = page_productions.map(production => mapRow(production));

            this.setState({
                total_count,
                total_quantity_produced,
                total_quantity_scrap,
                rows,
            });
        } catch (error: unknown) {
            this.setState({ error: (error as Error).message });
        }

        this.setState({
            is_loading: false,
            is_updating: false,
        });
    };

    loadExportData = async (
        export_all: boolean,
        filters: Filters<TableRow>,
        order_by: ColumnId<TableRow>,
        order_desc: boolean
    ): Promise<TableExportData<TableRow>> => {
        let rows: TableRow[] | null = this.state.rows;
        if (export_all) {
            try {
                const { productions } = await getBackend().manufacturing.filterProduced(
                    getFiterRequestParams(filters, undefined, undefined, order_by, order_desc, false, false)
                );
                rows = productions.map(production => mapRow(production));
            } catch {
                rows = null;
            }
        }

        return {
            rows,
            title: translate("Header.menu.productions", "Productions"),
            file_name: "productions",
        };
    };

    getColumns = (): TableColumn<TableRow>[] => {
        const columns: TableColumn<TableRow>[] = [
            {
                id: "order_external_id",
                title: translate("OrderTableProduction.order_id", "Order ID"),
                Header: TextFilterWithReset,
                Cell: props => <ExternalIdCell {...props} onShowOrderModal={this.handleOrderModalShow} />,
                Footer: props => (
                    <ExternalIdFooter
                        {...props}
                        total_quantity_produced={this.state.total_quantity_produced}
                        total_quantity_scope={this.state.total_quantity_scope}
                        onTotalQuantityScopeChange={this.handleTotalQuantityScopeChange}
                        onDataLoad={this.loadData}
                    />
                ),
            },
            {
                id: "process_num",
                title: translate("OrderTableProduction.process_num", "Process number"),
                Header: TextFilter,
            },
            {
                id: "external_operation_id",
                title: translate("OrderTableProduction.external_operation_id", "Operation ID"),
                Header: TextFilter,
            },
            {
                id: "order_title",
                title: translate("common.operation", "Operation"),
                Header: TextFilter,
            },
            {
                id: "material_external_id",
                title: translate("OrderTableProduction.material_external_id", "Material ID"),
                Header: TextFilter,
            },
            {
                id: "material_title",
                title: translate("common.material", "Material"),
                Header: TextFilter,
                Cell: MaterialTitleCell,
                formatExport: formatMaterialTitle,
            },
            {
                id: "line_external_id",
                title: translate("OrderTableProduction.line_external_id", "Line ID"),
                Header: TextFilter,
            },
            {
                id: "line_title",
                title: translate("common.line", "Line"),
                Header: TextFilter,
            },
            {
                id: "plant_external_id",
                title: translate("common.plant_id", "Plant ID"),
                Header: TextFilter,
            },
            {
                id: "plant_title",
                title: translate("common.plant", "Plant"),
                Header: TextFilter,
            },
            {
                id: "produced_start",
                title: translate("OrderTableProduction.time_start", "Start time"),
                Header: DateRangeFilter,
                Cell: DateTimeCell,
                formatExport: formatDateTime,
            },
            {
                id: "produced_end",
                title: translate("OrderTableProduction.time_end", "End time"),
                Header: DateRangeFilter,
                Cell: DateTimeCell,
                formatExport: formatDateTime,
            },
            {
                id: "ts",
                title: translate("OrderTableProduction.produced_at", "Produced at"),
                Cell: DateTimeCell,
                formatExport: formatDateTime,
            },
            {
                id: "available_in_ts",
                title: translate("OrderTableProduction.available_at", "Available at"),
                Cell: DateTimeCell,
                formatExport: formatDateTime,
            },
            {
                id: "quantity_produced",
                title: translate("OrderTableProduction.quantity_produced", "Quantity produced"),
                Cell: FloatCell,
                Footer: FloatFooter,
            },
            {
                id: "quantity_scrap",
                title: translate("common.scrap", "Scrap"),
                Cell: FloatCell,
                Footer: FloatFooter,
            },
            {
                id: "status",
                title: translate("common.status", "Status"),
                Header: MultiSelectFilter,
                Cell: StatusCell,
                formatExport: formatStatus,
                filter_options: [
                    {
                        value: PRODUCTION_STATUSES.confirmed,
                        label: translate("common.confirmed", "Confirmed"),
                    },
                    {
                        value: PRODUCTION_STATUSES.unconfirmed,
                        label: translate("common.unconfirmed", "Unconfirmed"),
                    },
                ],
            },
            {
                id: "stock_accounted_for",
                title: translate("OrderTableProduction.stock_accounted_for", "Stock accounted for"),
                Header: SelectFilter,
                Cell: BooleanCell,
                formatExport: formatBoolean,
                filter_options: [
                    { value: String(false), label: translate("common.no", "No") },
                    { value: String(true), label: translate("common.yes", "Yes") },
                ],
                disable_sort: true,
            },
            {
                id: "uuid",
                title: translate("common.uuid", "UUID"),
            },
        ];

        const details_columns: Set<ColumnId<TableRow>> = new Set(["uuid"]);

        return columns.filter(column => this.state.show_details || !details_columns.has(column.id));
    };

    getFooter = (): TableFooter<TableRow> => {
        const { rows, total_quantity_produced, total_quantity_scrap, total_quantity_scope } = this.state;
        let quantity_produced = total_quantity_produced;
        let quantity_scrap = total_quantity_scrap;

        if (total_quantity_scope === QUANTITY_SCOPE.page) {
            quantity_produced = 0;
            quantity_scrap = 0;

            for (const row of rows) {
                quantity_produced += row.quantity_produced;
                quantity_scrap += row.quantity_scrap;
            }
        }

        return {
            order_external_id: translate("common.sum", "Sum"),
            quantity_produced,
            quantity_scrap,
        } as TableFooter<TableRow>;
    };

    handleDataUpdate = async (
        filters: Filters<TableRow>,
        page_index: number,
        page_size: number,
        order_by: ColumnId<TableRow>,
        order_desc: boolean
    ) => {
        await this.loadData(filters, page_index, page_size, order_by, order_desc, true);
    };

    handleTotalCountUpdate = async (filters: Filters<TableRow>) => {
        const { count } = await getBackend().manufacturing.filterProduced(
            getFiterRequestParams(filters, 0, 0, undefined, false, true)
        );

        this.setState({ total_count: count ?? 0 });
    };

    handleFiltersChange = () => {
        this.setState({
            total_count: -1,
            total_quantity_produced: -1,
            total_quantity_scrap: -1,
            total_quantity_scope: QUANTITY_SCOPE.page,
        });
    };

    handleOrderModalShow = (row: TableRow) => {
        this.setState({
            show_order_modal: true,
            selected_row: row,
        });
    };

    handleOrderModalHide = () => {
        this.setState({ show_order_modal: false });
    };

    handleDetailsToggle = (show: boolean) => {
        this.setState({ show_details: show });
    };

    handleScroll = (event: React.UIEvent<HTMLElement>) => {
        if (event.currentTarget.scrollLeft > 50) {
            if (!this.state.is_scrolled_x) {
                this.setState({ is_scrolled_x: true });
            }
        } else if (this.state.is_scrolled_x) {
            this.setState({ is_scrolled_x: false });
        }
    };

    handleTotalQuantityScopeChange = (selected: ILabelValuePair) => {
        const total_quantity_scope = selected.value === QUANTITY_SCOPE.all ? QUANTITY_SCOPE.all : QUANTITY_SCOPE.page;
        this.setState({ total_quantity_scope });
    };

    handleSubmit = async (filters: Filters<TableRow>) => {
        this.setState({ default_filters: filters });
        await this.loadData(filters, 0, DEFAULT_PAGE_SIZE);
    };

    render() {
        const {
            default_filters,
            error,
            is_loading,
            is_updating,
            is_scrolled_x,
            rows,
            show_details,
            selected_row,
            show_order_modal,
            total_count,
        } = this.state;
        const is_admin = isInRole(ROLE_ADMIN);

        if (default_filters === null) {
            return (
                <DefaultFiltersForm
                    table_id={DATA_TABLE_ID}
                    title={translate("common.search_productions", "Search productions")}
                    getDefaultFilters={getDefaultFilters}
                    onSubmit={this.handleSubmit}
                />
            );
        }

        return (
            <>
                <LargeDataTable
                    id={DATA_TABLE_ID}
                    class_name={is_scrolled_x ? "is-scrolled-x" : ""}
                    error={error}
                    is_loading={is_loading}
                    is_updating={is_updating}
                    show_details={is_admin ? show_details : undefined}
                    total_count={total_count}
                    default_page_size={DEFAULT_PAGE_SIZE}
                    default_filters={default_filters}
                    columns={this.getColumns()}
                    rows={rows}
                    footer={this.getFooter()}
                    always_visible_columns={["order_external_id", "process_num", "material_external_id"]}
                    loadExportData={this.loadExportData}
                    onDataUpdate={this.handleDataUpdate}
                    onTotalCountUpdate={this.handleTotalCountUpdate}
                    onDetailsToggle={is_admin ? this.handleDetailsToggle : undefined}
                    onFiltersChange={this.handleFiltersChange}
                    onScroll={this.handleScroll}
                />
                {selected_row !== null && (
                    <OrderTableProductionModal
                        show={show_order_modal}
                        selected_order_uuid={selected_row.uuid}
                        selected_order_external_id={selected_row.order_external_id}
                        process_num={selected_row.process_num}
                        onShowOrHideOrderModal={this.handleOrderModalHide}
                        override_description_fields={["external_id", "material_external_id", "material_title"]}
                        show_columns={[
                            "process_num",
                            "title",
                            "quantity_produced",
                            "quantity_scrap",
                            "quantity_total",
                            "output_stock",
                            "status",
                            "skip_sim",
                            "ignore_on_planning_board",
                        ]}
                    />
                )}
            </>
        );
    }
}

export default Productions;
